import { useState, useEffect, useCallback } from 'react';

// Design-time tweaks panel. Mirrored verbatim from static/Tweaks.jsx,
// converted from script-tag globals to ESM. Only renders if a parent window
// posts {type: '__activate_edit_mode'} — invisible to real visitors.

function _autoLabel(key) {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, c => c.toUpperCase());
}

export function useTweaks({ defaults, groups, labels }) {
  const [tweaks, setTweaks] = useState(defaults);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (!e.data || typeof e.data !== 'object') return;
      if (e.data.type === '__activate_edit_mode')   setEditing(true);
      if (e.data.type === '__deactivate_edit_mode') setEditing(false);
    };
    window.addEventListener('message', handler);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', handler);
  }, []);

  const update = useCallback((key, value) => {
    setTweaks(t => ({ ...t, [key]: value }));
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits: { [key]: value } }, '*');
  }, []);

  const panel = editing
    ? <TweaksPanel tweaks={tweaks} groups={groups} labels={labels} update={update}/>
    : null;

  return { tweaks, editing, update, panel };
}

export function TweaksPanel({ tweaks, groups, labels, update }) {
  const [openGroups, setOpenGroups] = useState(() => {
    return new Set(groups.length ? [groups[0].name] : []);
  });
  const toggle = (name) => setOpenGroups(s => {
    const n = new Set(s);
    n.has(name) ? n.delete(name) : n.add(name);
    return n;
  });

  return (
    <div className="tweaks-panel">
      <div className="tweaks-panel__head">
        <div className="tweaks-panel__title">Tweaks</div>
        <div className="tweaks-panel__hint">Edit any text on the page</div>
      </div>
      <div className="tweaks-panel__body">
        {groups.map(g => {
          const open = openGroups.has(g.name);
          const longKeys = new Set(g.long || []);
          return (
            <div key={g.name} className={`tweaks-group ${open ? '' : 'tweaks-group--closed'}`}>
              <div className="tweaks-group__name" onClick={() => toggle(g.name)}>
                <span>{g.name}</span><span className="chev">▾</span>
              </div>
              <div className="tweaks-group__fields">
                {g.keys.map(k => {
                  const val = tweaks[k] || '';
                  const isLong = longKeys.has(k) || val.length > 80;
                  const label = (labels && labels[k]) || _autoLabel(k);
                  const choices = g.options && g.options[k];
                  return (
                    <div key={k} className="tweaks-field">
                      <label>{label}</label>
                      {choices
                        ? <select value={val} onChange={e => update(k, e.target.value)}>
                            {choices.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        : isLong
                          ? <textarea value={val} rows={3} onChange={e => update(k, e.target.value)}/>
                          : <input value={val} onChange={e => update(k, e.target.value)}/>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
